import React, { useState, useCallback } from "react";
import type { ShippingRule } from "@codway-dev/shared";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useProvinces } from "../../hooks/useProvinces";
import { ProvinceSelector } from "./ProvinceSelector";

export interface RateFormProps {
  rate: ShippingRule;
  currency: string;
  countryCode: string;
  canDelete: boolean;
  onUpdate: (id: string, updates: Partial<ShippingRule>) => void;
  onCollapse: () => void;
  onDelete: () => void;
}

export const RateForm: React.FC<RateFormProps> = ({
  rate,
  currency,
  countryCode,
  canDelete,
  onUpdate,
  onCollapse,
  onDelete,
}) => {
  const [showConditions, setShowConditions] = useState(
    !!(
      rate.conditions?.min_subtotal != null ||
      rate.conditions?.max_subtotal != null
    )
  );

  const selectedStateCodes = rate.conditions?.allowed_state_codes ?? [];
  const [enableStateFilter, setEnableStateFilter] = useState(
    selectedStateCodes.length > 0
  );

  const { provinces, loading: provincesLoading } = useProvinces(
    enableStateFilter ? countryCode : ""
  );

  const toggleStateCode = useCallback(
    (code: string) => {
      const current = rate.conditions?.allowed_state_codes ?? [];
      const next = current.includes(code)
        ? current.filter((c) => c !== code)
        : [...current, code];
      onUpdate(rate.id!, {
        conditions: { ...rate.conditions, allowed_state_codes: next },
      });
    },
    [rate, onUpdate]
  );

  const removeStateCode = useCallback(
    (code: string) => {
      const next = (rate.conditions?.allowed_state_codes ?? []).filter(
        (c) => c !== code
      );
      onUpdate(rate.id!, {
        conditions: { ...rate.conditions, allowed_state_codes: next },
      });
    },
    [rate, onUpdate]
  );

  const cost = rate.action.type === "flat_rate" ? rate.action.cost : 0;

  return (
    <s-box
      padding="large-200"
      background="base"
      border="small subdued solid"
      borderRadius="large-100"
    >
      <s-stack direction="block" gap="base">
        <s-grid gridTemplateColumns="1fr 1fr auto" gap="base" alignItems="end">
          <s-text-field
            label="Nombre de la tarifa"
            name={`rate_name_${rate.id}`}
            value={rate.name}
            placeholder="Ej: Envío estándar"
            onInput={(event: CallbackEvent<"s-text-field">) =>
              onUpdate(rate.id!, {
                name: event.currentTarget.value,
              })
            }
          />
          <s-text-field
            label="Descripción de la tarifa"
            name={`rate_desc_${rate.id}`}
            value={rate.description ?? ""}
            placeholder="Opcional"
            onInput={(event: CallbackEvent<"s-text-field">) =>
              onUpdate(rate.id!, {
                description: event.currentTarget.value || undefined,
              })
            }
          />
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-text color="subdued">{currency}</s-text>
            <s-number-field
              label="Precio"
              name={`rate_price_${rate.id}`}
              value={String(cost)}
              min={0}
              placeholder="0,00"
              onInput={(event: CallbackEvent<"s-number-field">) => {
                const val = parseFloat(event.currentTarget.value);
                onUpdate(rate.id!, {
                  action: {
                    type: "flat_rate",
                    cost: isNaN(val) ? 0 : val,
                  },
                });
              }}
            />
          </s-stack>
        </s-grid>

        {/* Conditions */}
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Condiciones de la tarifa</s-text>
          {!showConditions ? (
            <s-button
              variant="secondary"
              onClick={() => setShowConditions(true)}
            >
              + Añadir condición
            </s-button>
          ) : (
            <s-grid gridTemplateColumns="1fr 1fr" gap="base">
              <s-text-field
                label="Subtotal mínimo"
                name={`rate_min_${rate.id}`}
                value={
                  rate.conditions?.min_subtotal != null
                    ? String(rate.conditions.min_subtotal)
                    : ""
                }
                placeholder="0"
                onInput={(event: CallbackEvent<"s-text-field">) => {
                  const val = parseFloat(event.currentTarget.value);
                  onUpdate(rate.id!, {
                    conditions: {
                      ...rate.conditions,
                      min_subtotal: isNaN(val) ? undefined : val,
                    },
                  });
                }}
              />
              <s-text-field
                label="Subtotal máximo"
                name={`rate_max_${rate.id}`}
                value={
                  rate.conditions?.max_subtotal != null
                    ? String(rate.conditions.max_subtotal)
                    : ""
                }
                placeholder="Sin límite"
                onInput={(event: CallbackEvent<"s-text-field">) => {
                  const val = parseFloat(event.currentTarget.value);
                  onUpdate(rate.id!, {
                    conditions: {
                      ...rate.conditions,
                      max_subtotal: isNaN(val) ? undefined : val,
                    },
                  });
                }}
              />
            </s-grid>
          )}
        </s-stack>

        {/* Province / State filter */}
        <s-checkbox
          {...({
            label:
              "Habilite esta tarifa solo para provincias / estados específicos",
            checked: enableStateFilter,
            onChange: () => {
              const next = !enableStateFilter;
              setEnableStateFilter(next);
              if (!next) {
                onUpdate(rate.id!, {
                  conditions: {
                    ...rate.conditions,
                    allowed_state_codes: undefined,
                  },
                });
              }
            },
          } as Record<string, unknown>)}
        />

        {enableStateFilter && (
          <ProvinceSelector
            rateId={rate.id!}
            provinces={provinces}
            loading={provincesLoading}
            selectedCodes={selectedStateCodes}
            onToggle={toggleStateCode}
            onRemove={removeStateCode}
          />
        )}

        {/* Row actions */}
        <s-stack direction="inline" gap="small-200">
          <s-button variant="primary" onClick={onCollapse}>
            Confirmar
          </s-button>
          {canDelete && (
            <s-button variant="secondary" tone="critical" onClick={onDelete}>
              Eliminar
            </s-button>
          )}
        </s-stack>
      </s-stack>
    </s-box>
  );
};
